import { TrendingUp, TrendingDown, MapPin, MoreHorizontal, Loader2 } from "lucide-react";
import { DemoHint } from "@/components/demo/DemoHint";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useLaneData, type LaneData } from "@/hooks/useLaneData";

interface AIRecommendationsPanelProps {
  equipment?: string;
  minMiles?: number;
  maxMiles?: number;
  lanes?: LaneData[];
  isLoading?: boolean;
  mode?: "broker" | "carrier";
}

export function AIRecommendationsPanel({ equipment, minMiles = 500, maxMiles, lanes: externalLanes, isLoading: externalLoading, mode = "broker" }: AIRecommendationsPanelProps) {
  const navigate = useNavigate();

  // Use external lanes if provided, otherwise fetch with filters
  const { lanes: fetchedLanes, isLoading: fetchedLoading } = useLaneData(
    externalLanes ? undefined : equipment,
    externalLanes ? undefined : minMiles,
    externalLanes ? undefined : maxMiles
  );

  const lanes = externalLanes ?? fetchedLanes;
  const isLoading = externalLoading ?? fetchedLoading;

  // Sort by volume (load count) descending for genuine demand signal
  const sortedLanes = [...lanes].sort((a, b) => b.volume - a.volume).slice(0, 5);

  const handleLaneClick = (lane: LaneData) => {
    const params = new URLSearchParams();
    params.set('origin', `${lane.originCity}, ${lane.originState}`);
    params.set('destination', `${lane.destCity}, ${lane.destState}`);
    if (equipment) params.set('equipment', equipment);
    params.set('source', 'ai-recommendations');
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="flex flex-col rounded-lg border bg-card overflow-hidden h-full">
      <div className="flex items-center justify-between border-b bg-muted/20 px-4 py-2 flex-shrink-0">
        <DemoHint hintKey="section.aiRecommendations" side="bottom">
          <h4 className="text-[12px] font-bold text-foreground">
            {mode === "carrier" ? "Best Lanes to Run" : "Today's AI Recommendations"}
          </h4>
        </DemoHint>
        <button className="text-muted-foreground hover:text-foreground transition-colors">
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center justify-between border-b bg-muted/10 px-4 py-1.5 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold text-foreground">
            Top {sortedLanes.length > 0 ? sortedLanes.length : ''} {mode === "carrier" ? "Lanes To Run" : "Lanes To Sell"}
          </span>
          {sortedLanes.length > 0 && (
            <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={{ backgroundColor: "hsl(var(--brand-teal-subtle))", color: "hsl(var(--brand-teal-deep))" }}>
              {mode === "carrier" ? "High Volume" : "High Demand"}
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span className="text-[10px]">Analyzing lanes…</span>
          </div>
        ) : sortedLanes.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <span className="text-[10px]">No lane data available yet</span>
          </div>
        ) : (
          sortedLanes.map((lane, i) => {
            const avgRate = lane.volume > 0 ? Math.round(lane.totalAmount / lane.volume) : 0;
            const originPart = lane.name.split(' → ')[0] || '—';

            return (
              <div
                key={i}
                onClick={() => handleLaneClick(lane)}
                className={cn("flex items-stretch gap-0 hover:bg-muted/20 transition-colors cursor-pointer", i < sortedLanes.length - 1 && "border-b")}
              >
                <div className="flex-1 px-4 py-2.5 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[12px] font-bold text-foreground truncate">{lane.shortName}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">{lane.volume} load{lane.volume !== 1 ? 's' : ''} • {Math.round(lane.totalMiles).toLocaleString()} mi total</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    {lane.isTop ? (
                      <TrendingUp className="h-3 w-3 text-success" />
                    ) : (
                      <TrendingDown className="h-3 w-3 text-muted-foreground" />
                    )}
                    <span className="text-[11px] font-bold" style={{ color: lane.isTop ? "hsl(var(--success))" : "hsl(var(--muted-foreground))" }}>
                      {lane.rateDisplay}/mi
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {lane.isTop ? "Top performing lane" : "Active lane"}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col justify-center items-end gap-1 border-l px-4 py-2.5 min-w-[130px]">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 text-accent" />
                    <span className="text-[11px] font-semibold text-foreground truncate max-w-[100px]">{originPart}</span>
                  </div>
                  <span className="rounded-md px-3 py-1 text-[13px] font-bold" style={{ backgroundColor: "hsl(var(--brand-teal-deep))", color: "#ffffff" }}>
                    ${avgRate.toLocaleString()}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{lane.rateDisplay}/mi</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
